import React, { useState } from "react";
import {
  Box,
  Divider,
  IconButton,
  InputBase,
  Typography,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { RootState } from "utils/store";
import { setReduxSignRequest, setScheduleTime } from "redux/appSlice";
import { convertEstToLocalTimezone } from "helpers/methods";
import MobileDelayDateTimeSheet from "../components/MobileDelayDateTimeSheet";
import { MobileSignRequestStyles } from "../MobileSignRequest.styles";

const SUBJECT_MAX = 100;
const MESSAGE_MAX = 1000;

const MobileSend: React.FC = () => {
  const theme = useTheme();
  const m = theme.palette.mobile;
  const classes = MobileSignRequestStyles(theme);
  const dispatch = useAppDispatch();
  const signRequest = useAppSelector((s: RootState) => s.app.signRequest);

  const [openSchedule, setOpenSchedule] = useState(false);

  const subject = signRequest.emailSubject || "";
  const message = signRequest.emailMessage || "";
  const scheduleTime = signRequest.scheduleTime;

  const scheduledLabel = scheduleTime
    ? convertEstToLocalTimezone(scheduleTime, "MM/DD/YYYY HH:mm:ss").format(
        "MMM D, YYYY h:mm A",
      )
    : "";

  // Same redux update pattern as the desktop RequestConfiguration.
  const handleEmailContent = (event: React.ChangeEvent<any>) => {
    const { name, value } = event.target;
    dispatch(setReduxSignRequest({ [name]: value }));
  };

  // Reusable label above each field (Figma: uppercase, base-900, 12px medium).
  const fieldLabel = {
    fontSize: "12px",
    fontWeight: 500,
    color: m.base[900],
    lineHeight: 1.2,
  };
  const counterText = {
    fontSize: "12px",
    fontWeight: 500,
    color: m.grey[400],
    whiteSpace: "nowrap" as const,
  };
  const fieldBox = {
    border: `1px solid ${m.grey[50]}`,
    borderRadius: "8px",
    p: 2,
  };
  const inputSx = {
    p: 0,
    fontSize: "14px",
    fontWeight: 500,
    color: m.base[900],
    lineHeight: "20px",
    "& input, & textarea": { p: 0 },
    "& input::placeholder, & textarea::placeholder": {
      color: m.grey[200],
      opacity: 1,
    },
  };

  return (
    <Box>
      {/* Schedule card — becomes a "Scheduled for" chip once a date is set */}
      {scheduleTime ? (
        <Box sx={{ ...classes.delayChip, mb: 2 }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={classes.delayChipLabel}>Scheduled for</Typography>
            <Typography sx={classes.delayChipValue}>{scheduledLabel}</Typography>
          </Box>
          <Box
            sx={{ display: "flex", alignItems: "center", gap: 1.5, flexShrink: 0 }}
          >
            <IconButton
              size="small"
              aria-label="Edit schedule"
              sx={{ p: 0, color: m.base[500] }}
              onClick={() => setOpenSchedule(true)}
            >
              <EditIcon sx={{ fontSize: 20 }} />
            </IconButton>
            <IconButton
              size="small"
              aria-label="Remove schedule"
              sx={{ p: 0, color: m.base[500] }}
              onClick={() => dispatch(setScheduleTime(undefined))}
            >
              <CloseIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Box>
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 1.5,
            bgcolor: m.ePurple[50],
            borderRadius: "16px",
            p: 2,
            mb: 2,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{ fontSize: "14px", fontWeight: 700, color: m.base[900] }}
            >
              Schedule sign request
            </Typography>
            <Typography
              sx={{
                fontSize: "12px",
                fontWeight: 500,
                color: m.grey[400],
                mt: 0.25,
              }}
            >
              Schedule document signing request
            </Typography>
          </Box>
          <Box
            component="button"
            type="button"
            onClick={() => setOpenSchedule(true)}
            sx={{
              flexShrink: 0,
              cursor: "pointer",
              bgcolor: m.surface.white,
              border: `1px solid ${m.base[500]}`,
              borderRadius: "12px",
              px: 2,
              py: 1.5,
              fontSize: "12px",
              fontWeight: 700,
              color: m.base[500],
              fontFamily: "inherit",
            }}
          >
            Schedule
          </Box>
        </Box>
      )}

      {/* Email content card */}
      <Box
        sx={{
          border: `1px solid ${m.grey[50]}`,
          borderRadius: "16px",
          p: 2,
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
        }}
      >
        <Box>
          <Typography
            sx={{ fontSize: "14px", fontWeight: 700, color: m.base[900] }}
          >
            Email content
          </Typography>
          <Typography
            sx={{
              fontSize: "12px",
              fontWeight: 500,
              color: m.grey[400],
              mt: 0.25,
            }}
          >
            Sent to all signers
          </Typography>
        </Box>

        {/* Full-bleed divider (edge-to-edge, ignoring the card's 16px padding) */}
        <Divider sx={{ borderColor: m.grey[50], mx: -2 }} />

        {/* Subject */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          <Typography
            component="label"
            htmlFor="mobile-email-subject"
            sx={fieldLabel}
          >
            SUBJECT LINE
          </Typography>
          <Box
            sx={{
              ...fieldBox,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
            }}
          >
            <InputBase
              id="mobile-email-subject"
              name="emailSubject"
              value={subject}
              onChange={handleEmailContent}
              placeholder="Enter subject"
              inputProps={{ maxLength: SUBJECT_MAX, "aria-label": "Subject line" }}
              sx={{ ...inputSx, flex: 1 }}
            />
            <Typography sx={counterText}>
              {subject.length}/{SUBJECT_MAX}
            </Typography>
          </Box>
        </Box>

        {/* Message */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          <Typography
            component="label"
            htmlFor="mobile-email-message"
            sx={fieldLabel}
          >
            MESSAGE(OPTIONAL)
          </Typography>
          <Box
            sx={{
              ...fieldBox,
              minHeight: 80,
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              gap: 1,
            }}
          >
            <InputBase
              id="mobile-email-message"
              name="emailMessage"
              value={message}
              onChange={handleEmailContent}
              placeholder="Add a personal note to your signers"
              multiline
              minRows={1}
              maxRows={6}
              inputProps={{
                maxLength: MESSAGE_MAX,
                "aria-label": "Message (optional)",
              }}
              sx={{ ...inputSx, width: "100%", alignItems: "flex-start" }}
            />
            <Typography sx={{ ...counterText, textAlign: "right", width: "100%" }}>
              {message.length}/{MESSAGE_MAX}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Schedule: same date/time sheet as "delay by date and time" */}
      <MobileDelayDateTimeSheet
        open={openSchedule}
        onClose={() => setOpenSchedule(false)}
        title="Schedule"
        isScheduleReq
      />
    </Box>
  );
};

export default MobileSend;
